import { Stack, FlexBox } from '@ardoq/layout';
import { FlexHeader, Header } from '../atoms';
import { DragAndDropSectionsProvider } from '@ardoq/drag-and-drop';
import { Group } from './PerspectiveGroup';
import { AddPerspectiveGroupButton } from './AddPerspectiveGroupButton';
import {
  GetInvalidGroupingSelectOptions,
  GetRuleNotificationParams,
  PerspectiveGroupingRule,
  PerspectivesGroupsOptions,
  GetGroupDisabledProps,
} from './types';
import { DisabledOptionProps, NotificationData } from '../types';
import { GroupNameHeaderInfoIcon } from './GroupNameHeaderInfoIcon';
import { TabNotification } from '../TabNotification';
import { type I18nLocale, t } from '@ardoq/i18n';

type PerspectivesGroupsProps = {
  groupingOptions: PerspectivesGroupsOptions;
  groupingRules: PerspectiveGroupingRule[];
  getRuleNotificationParams?: GetRuleNotificationParams;
  onGroupingRuleReordered: (
    reorderedGroupingRules: PerspectiveGroupingRule[]
  ) => void;
  onGroupingRuleDeleted: (groupingRuleId: string) => void;
  onGroupingRuleAdded: (newGroupingRule: PerspectiveGroupingRule) => void;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  disabledOptionProps?: DisabledOptionProps;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  restrictedGroupFormattingWarningProps?: NotificationData;
  disableAddPerspectiveGroupButton?: boolean;
  getGroupDisabledProps?: GetGroupDisabledProps;
  isGroupingByTypeNamesEnabled?: boolean;
  i18nLocale: I18nLocale;
};

export const PerspectivesGroups = ({
  groupingOptions,
  groupingRules,
  onGroupingRuleReordered,
  onGroupingRuleDeleted,
  onGroupingRuleAdded,
  onGroupingRuleUpdated,
  getRuleNotificationParams,
  disabledOptionProps,
  getInvalidGroupingSelectOptions,
  restrictedGroupFormattingWarningProps,
  disableAddPerspectiveGroupButton,
  getGroupDisabledProps,
  isGroupingByTypeNamesEnabled,
  i18nLocale,
}: PerspectivesGroupsProps) => {
  const groupRulesIndexedById = Object.fromEntries(
    groupingRules.map(group => [group.id, group])
  );

  const reorderGroupings = (reorderedGroupingRulesIds: string[]) => {
    const reorderedGroupingRules = reorderedGroupingRulesIds.map(
      id => groupRulesIndexedById[id]
    );
    onGroupingRuleReordered(reorderedGroupingRules);
  };

  return (
    <Stack gap="medium">
      <TabNotification
        notificationData={restrictedGroupFormattingWarningProps}
      />
      {(groupingRules.length || null) && (
        <FlexBox gap="small" paddingX="small">
          <Header style={{ width: 132, paddingLeft: 32 }}>
            {t(
              i18nLocale,
              'perspectives:perspectivesGroups.groupBy',
              'Group by'
            )}
          </Header>
          <FlexHeader>
            {t(
              i18nLocale,
              'perspectives:perspectivesGroups.inWorkspace',
              'In workspace'
            )}
          </FlexHeader>
          <FlexHeader>
            {t(i18nLocale, 'perspectives:perspectivesGroups.type', 'Type')}
          </FlexHeader>
          <FlexHeader>
            {t(
              i18nLocale,
              'perspectives:perspectivesGroups.direction',
              'Direction'
            )}
          </FlexHeader>
          <FlexHeader>
            {t(
              i18nLocale,
              'perspectives:perspectivesGroups.groupName',
              'Group name'
            )}
            <GroupNameHeaderInfoIcon />
          </FlexHeader>
          <div style={{ width: 36 }} />
        </FlexBox>
      )}
      <DragAndDropSectionsProvider onDrop={reorderGroupings}>
        {groupingRules.map(groupingRules => (
          <Group
            onGroupingRuleDeleted={onGroupingRuleDeleted}
            onGroupingRuleUpdated={onGroupingRuleUpdated}
            dragAndDropCardId={groupingRules.id}
            groupingOptions={groupingOptions}
            key={groupingRules.id}
            groupingRule={groupingRules}
            getRuleNotificationParams={getRuleNotificationParams}
            disabledOptionProps={disabledOptionProps}
            getInvalidGroupingSelectOptions={getInvalidGroupingSelectOptions}
            isGroupingByTypeNamesEnabled={isGroupingByTypeNamesEnabled}
            i18nLocale={i18nLocale}
            {...getGroupDisabledProps?.(groupingRules)}
          />
        ))}
      </DragAndDropSectionsProvider>
      <div>
        <AddPerspectiveGroupButton
          groupingOptions={groupingOptions}
          onGroupingRuleAdded={onGroupingRuleAdded}
          disabledOptionProps={disabledOptionProps}
          isDisabled={disableAddPerspectiveGroupButton}
          buttonLabel={t(
            i18nLocale,
            'perspectives:perspectivesGroups.addGrouping',
            'Add grouping'
          )}
          isGroupingByTypeNamesEnabled={isGroupingByTypeNamesEnabled}
          i18nLocale={i18nLocale}
        />
      </div>
    </Stack>
  );
};
